import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import colors from '../colors';
import config from '../config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { IonIcon } from '@ionic/react';
import { chevronDownOutline, chevronUpOutline, downloadOutline } from 'ionicons/icons';
import { 
  IonButton, 
  IonContent, 
  IonItem, 
  IonLabel, 
  IonTextarea, 
  IonPage,
  IonSelect,
  IonSelectOption,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonCheckbox,
  IonInput,
  IonBadge,
  IonSpinner,
  IonModal,
  IonAlert,
  IonHeader,
  IonToolbar,
  IonTitle
} from '@ionic/react';
import './ExercisePlan.css';

// Helper function to get exercise link
// Uses YouTube search for reliable, free access to exercise demonstration videos
// YouTube has extensive exercise content with proper form demonstrations
const getExerciseLink = (exerciseName) => {
  if (!exerciseName) return null;
  
  // Use YouTube search - very reliable and free, with extensive exercise video content
  // Format: https://www.youtube.com/results?search_query=exercise+name+how+to
  const searchQuery = encodeURIComponent(`${exerciseName} how to`);
  return `https://www.youtube.com/results?search_query=${searchQuery}`;
};

// Helper function to get exercise link label
const getExerciseLinkLabel = (link) => {
  if (!link) return null;
  if (link.includes('bodybuilding.com')) return 'Bodybuilding.com';
  if (link.includes('youtube.com')) return 'YouTube';
  if (link.includes('exrx.net')) return 'ExRx.net';
  if (link.includes('musclewiki.com')) return 'MuscleWiki';
  return 'Exercise Guide';
};

const ExercisePlan = ({ member_id, user_id, auth_token, noPageWrapper = false }) => {
  // State for auth data
  const [effectiveMemberId, setEffectiveMemberId] = useState(member_id || localStorage.getItem('member_id') || null);
  const [effectiveUserId, setEffectiveUserId] = useState(user_id || localStorage.getItem('user_id') || null);
  const [effectiveAuthToken, setEffectiveAuthToken] = useState(auth_token || localStorage.getItem('auth_token') || null);

  // Sync auth props to state when they change
  useEffect(() => {
    if (member_id) {
      setEffectiveMemberId(member_id);
    }
    if (user_id) {
      setEffectiveUserId(user_id);
    }
    if (auth_token) {
      setEffectiveAuthToken(auth_token);
    }
  }, [member_id, user_id, auth_token]);

  // Track previous member_id to detect changes
  const previousMemberIdRef = useRef(null);

  // Reset form and data when member changes
  useEffect(() => {
    // Check if this is a different member (member changed)
    if (effectiveMemberId && previousMemberIdRef.current && previousMemberIdRef.current !== effectiveMemberId) {
      // Member changed - reset all form fields and data
      setPrimaryGoal('');
      setExistingExercises('');
      setWorkoutFrequency(3);
      setRestrictions('');
      setEmailToMember(true);
      setAvailableExercises([]);
      setExercisePlan(null);
      setMemberFullName('');
      setPromptsUsed({ system: '', user: '' });
      setShowPrompts(false);
      setAvailablePlans([]);
      setSelectedPlanId('new');
    }
    // Update ref for next comparison
    if (effectiveMemberId) {
      previousMemberIdRef.current = effectiveMemberId;
    }
  }, [effectiveMemberId]);

  // Form state
  const [primaryGoal, setPrimaryGoal] = useState('');
  const [existingExercises, setExistingExercises] = useState('');
  const [workoutFrequency, setWorkoutFrequency] = useState(3);
  const [restrictions, setRestrictions] = useState('');
  const [emailToMember, setEmailToMember] = useState(true);
  const [memberFullName, setMemberFullName] = useState('');
  
  // Data state
  const [availableExercises, setAvailableExercises] = useState([]);
  const [exercisePlan, setExercisePlan] = useState(null);
  
  // UI state
  const [isLoadingExercises, setIsLoadingExercises] = useState(false);
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
  const [showPrompts, setShowPrompts] = useState(false);
  const [promptsUsed, setPromptsUsed] = useState({ system: '', user: '' });
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('new');
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [isLoadingPlanDetails, setIsLoadingPlanDetails] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Ref for PDF printing
  const printRef = useRef();

  useEffect(() => {
    const fetchMemberName = async () => {
      if (!effectiveMemberId || !effectiveAuthToken) {
        setMemberFullName('');
        return;
      }

      try {
        const response = await fetch(`${config.apiUrl}/members_names`, {
          headers: {
            'Authorization': `Bearer ${effectiveAuthToken}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          setMemberFullName('');
          return;
        }

        const data = await response.json();
        const members = Array.isArray(data) ? data : [];
        const matchedMember = members.find((member) => String(member.id) === String(effectiveMemberId));
        if (!matchedMember) {
          setMemberFullName('');
          return;
        }

        const fullName = `${matchedMember.First_name || ''} ${matchedMember.Surname || ''}`.trim();
        setMemberFullName(fullName);
      } catch (error) {
        console.error('Error fetching member name:', error);
        setMemberFullName('');
      }
    };

    fetchMemberName();
  }, [effectiveMemberId, effectiveAuthToken]);

  const formatPlanDate = (plan) => {
    const createdAt = plan?.created_at ? new Date(plan.created_at) : null;
    return createdAt
      ? createdAt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      : 'Unknown date';
  };

  const planLabels = useMemo(() => {
    const countsByDate = {};
    const labelsById = {};

    availablePlans.forEach((plan) => {
      const dateLabel = formatPlanDate(plan);
      const count = (countsByDate[dateLabel] || 0) + 1;
      countsByDate[dateLabel] = count;
      const suffix = count > 1 ? ` (${count})` : '';
      labelsById[plan.id] = `Plan generated on ${dateLabel}${suffix}`;
    });

    return labelsById;
  }, [availablePlans]);

  const normalizeExercisePlan = (plan) => {
    if (!plan || !Array.isArray(plan.days)) {
      return null;
    }

    const validatedPlan = plan.days.map((day, index) => {
      let description = day.Description || [];
      if (typeof description === 'string') {
        description = [{ order: 1, type: 'general', description: description }];
      } else if (!Array.isArray(description)) {
        description = [];
      }

      const normalizedDescription = description.map((item, idx) => {
        const normalizedItem = {
          order: typeof item.order === 'number' ? item.order : idx + 1,
          type: item.type || 'general',
          description: item.description || ''
        };

        if (item.exercises && Array.isArray(item.exercises)) {
          normalizedItem.exercises = item.exercises.map((exercise, exIdx) => ({
            order: typeof exercise.order === 'number' ? exercise.order : exIdx + 1,
            name: exercise.name || `Exercise ${exIdx + 1}`,
            description: exercise.description || '',
            link: exercise.link || null
          })).sort((a, b) => a.order - b.order);
        }

        return normalizedItem;
      }).sort((a, b) => a.order - b.order);

      return {
        Day: day.Day || `Day ${index + 1}`,
        Overview: day.Overview || 'Rest Day / Active Recovery',
        Description: normalizedDescription,
        EstimatedCalories: typeof day.EstimatedCalories === 'number' ? day.EstimatedCalories : 0,
        TargetAreas: Array.isArray(day.TargetAreas) ? day.TargetAreas : []
      };
    });

    return {
      PlanDescription: plan.PlanDescription || 'Your personalised 14-day exercise plan has been designed to help you achieve your goals.',
      days: validatedPlan
    };
  };

  const extractExercisePlanFromResponse = (data) => {
    if (!data) return null;
    if (data.plan) return data.plan;
    if (data.exercise_plan) return data.exercise_plan;
    if (data.exercisePlan) return data.exercisePlan;
    if (Array.isArray(data.days) || data.PlanDescription) return data;
    return null;
  };

  const applyExerciseFormValues = (data) => {
    const primaryGoalValue = data?.primary_goal || data?.primaryGoal;
    if (primaryGoalValue) {
      setPrimaryGoal(primaryGoalValue);
    }

    const existingExercisesValue = data?.existing_exercises ?? data?.existingExercises;
    if (typeof existingExercisesValue === 'string') {
      setExistingExercises(existingExercisesValue);
    } else if (Array.isArray(existingExercisesValue)) {
      setExistingExercises(existingExercisesValue.join(', '));
    }

    const workoutFrequencyValue = data?.workout_frequency ?? data?.workoutFrequency;
    if (workoutFrequencyValue !== undefined && workoutFrequencyValue !== null) {
      const parsedFrequency = parseInt(workoutFrequencyValue, 10);
      setWorkoutFrequency(Number.isNaN(parsedFrequency) ? 3 : parsedFrequency);
    }

    const restrictionsValue = data?.restrictions;
    if (typeof restrictionsValue === 'string') {
      setRestrictions(restrictionsValue);
    }

    const emailToMemberValue = data?.email_to_member ?? data?.emailToMember;
    if (typeof emailToMemberValue === 'boolean') {
      setEmailToMember(emailToMemberValue);
    } else if (typeof emailToMemberValue === 'string') {
      const normalizedEmailValue = emailToMemberValue.trim().toLowerCase();
      if (normalizedEmailValue === 'true') {
        setEmailToMember(true);
      } else if (normalizedEmailValue === 'false') {
        setEmailToMember(false);
      }
    }
  };

  // PDF print handler - creates a new window with all content to bypass Ionic scroll constraints
  const handlePrint = () => {
    if (!exercisePlan) {
      toast.error('No content to print');
      return;
    }

    // Open a new window for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) {
      toast.error('Please allow popups to print the exercise plan');
      return;
    }

    // Get the color values from the colors object
    const colorValues = {
      blueGray: colors.blueGray,
      darkGray: colors.darkGray,
      warmGray: colors.warmGray,
      darkGreen: colors.darkGreen,
      lightPurpleGray: colors.lightPurpleGray
    };

    // Build HTML content from the exercise plan data
    const buildPlanHTML = () => {
      let html = `
        <div class="print-header">
          <img src="/infinity-logo.png" alt="Infinity Medical Concierge" class="company-logo" onerror="this.style.display='none'">
        </div>
        <h2>Your 14-Day Exercise Plan</h2>
        ${memberFullName ? `<div class="member-name">${memberFullName}</div>` : ''}
        <div class="ai-disclaimer">
          <strong>Important Notice:</strong> This exercise plan was generated by artificial intelligence (AI). 
          If you experience any pain, discomfort, dizziness, shortness of breath, or any other concerning symptoms 
          during exercise, <strong>stop immediately</strong> and contact Infinity Medical Concierge for medical guidance.
          Infinity Medical Concierge can also refer the Member to a biokineticist to help with a personalised exercise plan.
        </div>
      `;
      
      // Plan Description
      if (exercisePlan.PlanDescription) {
        html += `
          <div class="plan-description">
            <h3>About Your Plan</h3>
            <p>${exercisePlan.PlanDescription.replace(/\n/g, '<br>')}</p>
          </div>
        `;
      }
      
      // Days
      const days = exercisePlan.days || (Array.isArray(exercisePlan) ? exercisePlan : []);
      html += '<div class="plan-days">';
      
      days.forEach((day, index) => {
        html += `
          <div class="day-card">
            <div class="day-card-header">
              <div class="day-card-title">${day.Day || `Day ${index + 1}`}: ${day.Overview || 'Rest Day / Active Recovery'}</div>
              <div class="day-badges">
        `;
        
        if (day.EstimatedCalories > 0) {
          html += `<span class="badge">${day.EstimatedCalories} kcal</span>`;
        }
        
        if (day.TargetAreas && day.TargetAreas.length > 0) {
          day.TargetAreas.forEach(area => {
            html += `<span class="badge badge-secondary">${area}</span>`;
          });
        }
        
        html += `
              </div>
            </div>
            <div class="day-description">
        `;
        
        // Description segments
        if (Array.isArray(day.Description) && day.Description.length > 0) {
          day.Description.forEach((segment, segIndex) => {
            html += `
              <div class="description-segment">
                <div class="segment-header">
                  <span class="badge badge-type">${(segment.type || 'workout').charAt(0).toUpperCase() + (segment.type || 'workout').slice(1)}</span>
                  <span class="segment-order">Step ${segment.order || segIndex + 1}</span>
                </div>
                <div class="segment-description">${(segment.description || '').replace(/\n/g, '<br>')}</div>
            `;
            
            // Sub-exercises
            if (segment.exercises && segment.exercises.length > 0) {
              html += `
                <div class="exercise-section">
                  <div class="exercise-section-title">Exercises:</div>
                  <ul class="exercise-list">
              `;
              segment.exercises.forEach((exercise, exIndex) => {
                // Get exercise link - use provided link or generate one
                const exerciseLink = exercise.link || getExerciseLink(exercise.name);
                const linkLabel = getExerciseLinkLabel(exerciseLink);
                
                const linkHTML = exerciseLink 
                  ? ` <a href="${exerciseLink}" target="_blank" rel="noopener noreferrer" style="font-size: 10px; color: ${colorValues.blueGray}; text-decoration: none; font-weight: 500; margin-left: 6px;">[${linkLabel} ↗]</a>`
                  : '';
                
                html += `
                  <li class="exercise-item">
                    <strong>${exercise.name || `Exercise ${exIndex + 1}`}</strong>${linkHTML}
                    ${exercise.description ? `: ${exercise.description.replace(/\n/g, '<br>')}` : ''}
                  </li>
                `;
              });
              html += '</ul></div>';
            }
            
            html += '</div>';
          });
        } else if (day.Description) {
          // Fallback for string description
          html += `<div class="description-segment"><div class="segment-description">${String(day.Description).replace(/\n/g, '<br>')}</div></div>`;
        }
        
        html += `
            </div>
          </div>
        `;
      });
      
      html += '</div>';
      return html;
    };

    // Write the HTML content with embedded styles
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Exercise Plan - 14 Day Program</title>
          <meta charset="utf-8">
          <style>
            @page {
              margin: 20mm;
              size: A4;
            }
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              font-size: 14px;
              line-height: 1.6;
              color: #333;
              padding: 20px;
              background: white;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .print-header {
              margin-bottom: 30px;
              padding-bottom: 60px;
              border-bottom: 2px solid ${colorValues.blueGray};
              text-align: center;
            }
            .company-logo {
              max-width: 300px;
              max-height: 100px;
              margin-bottom: 30px;
              object-fit: contain;
            }
            .company-name {
              font-size: 18px;
              font-weight: 600;
              letter-spacing: 2px;
              color: ${colorValues.darkGray};
              text-transform: uppercase;
            }
            .ai-disclaimer {
              background: #fff3cd;
              border: 2px solid #ffc107;
              border-left: 4px solid #ff9800;
              border-radius: 8px;
              padding: 16px;
              margin-bottom: 24px;
              font-size: 13px;
              line-height: 1.7;
              color: #856404;
            }
            .ai-disclaimer strong {
              color: #d32f2f;
              font-weight: 600;
            }
            h2 {
              color: ${colorValues.darkGray};
              font-size: 24px;
              margin-top: 40px;
              margin-bottom: 24px;
            }
            .member-name {
              font-size: 14px;
              color: ${colorValues.warmGray};
              margin-top: -16px;
              margin-bottom: 24px;
              text-align: center;
            }
            h3 {
              color: ${colorValues.darkGray};
              font-size: 18px;
              margin-bottom: 12px;
            }
            .plan-days {
              display: block;
            }
            .day-card {
              page-break-inside: avoid;
              break-inside: avoid;
              margin-bottom: 20px;
              border: 1px solid #ddd;
              border-radius: 8px;
              padding: 16px;
              background: white;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .day-card-header {
              margin-bottom: 16px;
              padding-bottom: 12px;
              border-bottom: 1px solid #eee;
            }
            .day-card-title {
              font-size: 18px;
              font-weight: 600;
              margin-bottom: 8px;
              color: ${colorValues.darkGray};
            }
            .day-badges {
              margin-top: 8px;
            }
            .day-description {
              margin-top: 16px;
            }
            .description-segment {
              margin-bottom: 16px;
              padding: 12px;
              background: ${colorValues.lightPurpleGray}15;
              border-radius: 8px;
              border-left: 4px solid ${colorValues.blueGray};
            }
            .segment-header {
              display: flex;
              align-items: center;
              gap: 8px;
              margin-bottom: 8px;
            }
            .segment-order {
              font-size: 12px;
              color: ${colorValues.warmGray};
              font-weight: 500;
            }
            .segment-description {
              color: #333;
              white-space: pre-wrap;
            }
            .exercise-section {
              margin-top: 12px;
              padding-top: 12px;
              border-top: 1px solid ${colorValues.warmGray}66;
            }
            .exercise-section-title {
              font-size: 13px;
              font-weight: 600;
              color: ${colorValues.darkGray};
              margin-bottom: 8px;
            }
            .exercise-list {
              margin-left: 20px;
              margin-top: 8px;
            }
            .exercise-item {
              margin: 8px 0;
              line-height: 1.5;
            }
            .badge {
              display: inline-block;
              padding: 4px 8px;
              border-radius: 4px;
              margin-right: 4px;
              font-size: 12px;
              font-weight: 500;
              background: ${colorValues.blueGray};
              color: white;
            }
            .badge-secondary {
              background: ${colorValues.warmGray};
            }
            .badge-type {
              background: ${colorValues.blueGray};
              text-transform: capitalize;
            }
            .plan-description {
              margin-bottom: 24px;
              padding: 16px;
              background: #f5f5f5;
              border-radius: 8px;
              border-left: 4px solid ${colorValues.darkGreen};
            }
            .plan-description p {
              line-height: 1.8;
              margin-top: 8px;
            }
            @media print {
              body {
                padding: 0;
              }
              .print-header {
                page-break-after: avoid;
                break-after: avoid;
              }
              h2 {
                margin-top: 40px;
              }
              .day-card {
                page-break-inside: avoid;
                break-inside: avoid;
                box-shadow: none;
                border: 1px solid #ddd;
              }
              .ai-disclaimer {
                page-break-after: avoid;
                break-after: avoid;
              }
            }
          </style>
        </head>
        <body>
          ${buildPlanHTML()}
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                window.onafterprint = function() {
                  window.close();
                };
              }, 250);
            };
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
  };

  // Track effective auth data (from props or localStorage)
  useEffect(() => {
    if (member_id && user_id && auth_token) {
      localStorage.setItem('member_id', member_id);
      localStorage.setItem('user_id', user_id);
      localStorage.setItem('auth_token', auth_token);
      setEffectiveMemberId(member_id);
      setEffectiveAuthToken(auth_token);
    }
  }, [member_id, user_id, auth_token]);

  const fetchExistingPlans = useCallback(async () => {
    if (!effectiveMemberId || !effectiveAuthToken) {
      return;
    }

    setIsLoadingPlans(true);
    try {
      const url = `https://xuar-e6vh-cmcf.e2.xano.io/api:CpKwGPIS/exercise_plans/get/${effectiveMemberId}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${effectiveAuthToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        toast.error('Failed to load existing exercise plans');
        return;
      }

      const data = await response.json();
      const plans = Array.isArray(data) ? data : [];
      plans.sort((a, b) => (b.created_at || 0) - (a.created_at || 0));
      setAvailablePlans(plans);
    } catch (error) {
      console.error('Error fetching exercise plans:', error);
      toast.error('Failed to load existing exercise plans');
    } finally {
      setIsLoadingPlans(false);
    }
  }, [effectiveMemberId, effectiveAuthToken]);

  useEffect(() => {
    fetchExistingPlans();
    setSelectedPlanId('new');
    setExercisePlan(null);
  }, [fetchExistingPlans]);

  const handlePlanSelection = async (planId) => {
    setSelectedPlanId(planId);

    if (planId === 'new') {
      setExercisePlan(null);
      setPromptsUsed({ system: '', user: '' });
      setShowPrompts(false);
      setPrimaryGoal('');
      setExistingExercises('');
      setWorkoutFrequency(3);
      setRestrictions('');
      setEmailToMember(true);
      return;
    }

    if (!effectiveAuthToken) {
      toast.error('Missing authentication data');
      return;
    }

    setIsLoadingPlanDetails(true);
    try {
      const url = `https://xuar-e6vh-cmcf.e2.xano.io/api:CpKwGPIS/exercise_plans/get_plan/${planId}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${effectiveAuthToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        toast.error('Failed to load exercise plan details');
        return;
      }

      const data = await response.json();
      const planPayload = extractExercisePlanFromResponse(data);
      const normalizedPlan = normalizeExercisePlan(planPayload);

      if (!normalizedPlan) {
        toast.error('Invalid exercise plan structure received');
        return;
      }

      setExercisePlan(normalizedPlan);
      applyExerciseFormValues(data?.inputs ?? data);

      if (data.prompts) {
        setPromptsUsed(data.prompts);
      } else {
        setPromptsUsed({ system: '', user: '' });
      }
      setShowPrompts(false);
    } catch (error) {
      console.error('Error loading exercise plan:', error);
      toast.error('Failed to load exercise plan details');
    } finally {
      setIsLoadingPlanDetails(false);
    }
  };

  const handleDeletePlan = async () => {
    if (!selectedPlanId || selectedPlanId === 'new') {
      return;
    }

    if (!effectiveAuthToken) {
      toast.error('Missing authentication data');
      return;
    }

    try {
      const url = `https://xuar-e6vh-cmcf.e2.xano.io/api:CpKwGPIS/exercise_plans/delete/${selectedPlanId}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${effectiveAuthToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        toast.error('Failed to delete exercise plan');
        return;
      }

      toast.success('Exercise plan deleted');
      setSelectedPlanId('new');
      setExercisePlan(null);
      setPromptsUsed({ system: '', user: '' });
      setShowPrompts(false);
      setPrimaryGoal('');
      setExistingExercises('');
      setWorkoutFrequency(3);
      setRestrictions('');
      setEmailToMember(true);
      fetchExistingPlans();
    } catch (error) {
      console.error('Error deleting exercise plan:', error);
      toast.error('Failed to delete exercise plan');
    }
  };

  // Fetch exercise data to get unique exercise names
  useEffect(() => {
    const fetchExerciseData = async () => {
      if (!effectiveMemberId || !effectiveAuthToken) {
        return;
      }

      setIsLoadingExercises(true);
      try {
        const url = `https://xuar-e6vh-cmcf.e2.xano.io/api:2K9hwDeK/healthdata_physical_event/no_json/${effectiveMemberId}`;
        
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${effectiveAuthToken}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          console.error('Exercise data API response error:', {
            status: response.status,
            statusText: response.statusText,
            url
          });
          return;
        }
        
        const exerciseData = await response.json();
        
        // Extract unique exercise names from activity_type_name_string
        const uniqueExercises = new Set();
        exerciseData.forEach(exercise => {
          if (exercise.activity_type_name_string) {
            const exerciseName = exercise.activity_type_name_string.trim();
            // Filter out "Unknown (Unable to Detect Activity)"
            if (exerciseName && exerciseName !== 'Unknown (Unable to Detect Activity)') {
              uniqueExercises.add(exerciseName);
            }
          }
        });
        
        const exerciseList = Array.from(uniqueExercises).sort();
        setAvailableExercises(exerciseList);
        
        // Pre-populate existing exercises as comma-separated string
        if (exerciseList.length > 0) {
          setExistingExercises(exerciseList.join(', '));
        }
        
      } catch (err) {
        console.error('Error fetching exercise data:', err);
        toast.error('Failed to fetch exercise data');
      } finally {
        setIsLoadingExercises(false);
      }
    };

    fetchExerciseData();
  }, [effectiveMemberId, effectiveAuthToken]);

  // Generate exercise plan
  const handleGeneratePlan = async () => {
    if (!primaryGoal) {
      toast.error('Please select a primary goal');
      return;
    }

    if (!workoutFrequency || workoutFrequency < 1 || workoutFrequency > 7) {
      toast.error('Please enter a valid workout frequency (1-7 times per week)');
      return;
    }

    if (!effectiveMemberId || !effectiveAuthToken || !effectiveUserId) {
      toast.error('Missing authentication data');
      return;
    }

    setSelectedPlanId('new');
    // Show modal and clear the existing plan before generating a new one
    setShowGenerationModal(true);
    setExercisePlan(null);
    setIsGeneratingPlan(true);
    
    // Create AbortController for timeout (declared outside try for cleanup)
    const controller = new AbortController();
    let timeoutId = null;
    
    try {
      // Call backend API with 15-minute timeout
      const url = 'https://feedback-app-367524407367.europe-west2.run.app/exercise_plan';
      // const url = 'http://localhost:8000/exercise_plan';
      
      // Set timeout
      timeoutId = setTimeout(() => controller.abort(), 15 * 60 * 1000); // 15 minutes
      
      let response;
      try {
        response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            auth_token: effectiveAuthToken,
            member_id: effectiveMemberId,
            user_id: effectiveUserId,
            primary_goal: primaryGoal,
            existing_exercises: existingExercises,
            workout_frequency: workoutFrequency,
            restrictions: restrictions,
            email_to_member: emailToMember
          }),
          signal: controller.signal
        });
        
        // Clear timeout if request completes successfully
        clearTimeout(timeoutId);
      } catch (fetchError) {
        // Clear timeout on fetch error
        clearTimeout(timeoutId);
        
        // Re-throw to be handled by outer catch block
        throw fetchError;
      }

      // Handle 504 Gateway Timeout specifically
      if (response.status === 504) {
        toast.error('The server is taking longer than expected to generate your plan. The plan will be emailed to you when it\'s ready. Please check your email in a few minutes.');
        setIsGeneratingPlan(false);
        setShowGenerationModal(false);
        return;
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({ 
          error: `Server error (${response.status}): ${response.statusText || 'Failed to generate exercise plan'}` 
        }));
        toast.error(error.error || 'Failed to generate exercise plan');
        setIsGeneratingPlan(false);
        setShowGenerationModal(false);
        return;
      }

      const data = await response.json();
      const plan = data.plan; // Contains PlanDescription and days array

      // Validate the plan structure
      if (!plan || !plan.days || !Array.isArray(plan.days)) {
        toast.error('Invalid plan structure received from server');
        setIsGeneratingPlan(false);
        setShowGenerationModal(false);
        return;
      }

      if (plan.days.length !== 14) {
        toast.error(`Expected 14 days, got ${plan.days.length}`);
      }

      const normalizedPlan = normalizeExercisePlan(plan);

      if (!normalizedPlan) {
        toast.error('Invalid plan structure received from server');
        setIsGeneratingPlan(false);
        setShowGenerationModal(false);
        return;
      }

      setExercisePlan(normalizedPlan);
      
      // Store prompts if returned by backend
      if (data.prompts) {
        setPromptsUsed(data.prompts);
      } else {
        setPromptsUsed({ system: '', user: '' });
      }
      
      toast.success('Exercise plan generated successfully!');
      setShowGenerationModal(false);
      fetchExistingPlans();
    } catch (error) {
      console.error('Error generating exercise plan:', error);
      
      // Clear timeout if it exists
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      if (error.name === 'AbortError') {
        toast.error('Request timed out. The plan generation is taking longer than expected. The plan will be emailed to you when it\'s ready. Please check your email in a few minutes.');
      } else if (error.message === 'Failed to fetch' || error.message.includes('network') || error.message.includes('NetworkError')) {
        // Network errors often indicate gateway timeouts or connection issues
        toast.error('Connection error. The plan generation may still be processing. The plan will be emailed to you when it\'s ready. Please check your email in a few minutes.');
      } else {
        toast.error(`Failed to generate exercise plan: ${error.message || 'Unknown error occurred'}`);
      }
      setShowGenerationModal(false);
    } finally {
      setIsGeneratingPlan(false);
    }
  };

  const content = (
    <div className="exercise-plan-container">
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Exercise Plan Generator</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              {/* Form Section */}
              <div className="form-section">
                {/* Existing Plans */}
                {availablePlans.length > 0 && (
                  <>
                    <IonItem>
                      <IonLabel position="stacked">
                        Existing Plans
                        {isLoadingPlans && (
                          <span style={{ fontSize: '12px', color: colors.warmGray, marginLeft: '8px' }}>
                            (Loading...)
                          </span>
                        )}
                      </IonLabel>
                      <IonSelect
                        value={selectedPlanId}
                        placeholder="Create new plan"
                        onIonChange={(e) => handlePlanSelection(e.detail.value)}
                      >
                        <IonSelectOption value="new">Create new plan</IonSelectOption>
                        {availablePlans.map((plan) => (
                          <IonSelectOption key={plan.id} value={plan.id}>
                            {planLabels[plan.id] || `Plan generated on ${formatPlanDate(plan)}`}
                          </IonSelectOption>
                        ))}
                      </IonSelect>
                    </IonItem>
                    {selectedPlanId !== 'new' && (
                      <IonButton
                        color="danger"
                        fill="outline"
                        style={{ marginTop: '12px' }}
                        onClick={() => setShowDeleteConfirm(true)}
                        disabled={isLoadingPlanDetails}
                      >
                        Delete Selected Plan
                      </IonButton>
                    )}
                    {isLoadingPlanDetails && (
                      <div style={{ display: 'flex', alignItems: 'center', marginTop: '12px', color: colors.warmGray }}>
                        <IonSpinner name="crescent" style={{ marginRight: '8px' }} />
                        Loading plan details...
                      </div>
                    )}
                  </>
                )}

                {/* Primary Goal */}
                <IonItem>
                  <IonLabel position="stacked">Primary Goal *</IonLabel>
                  <IonSelect
                    value={primaryGoal}
                    placeholder="Select your primary goal"
                    onIonChange={(e) => setPrimaryGoal(e.detail.value)}
                  >
                    <IonSelectOption value="Weight Loss">Weight Loss</IonSelectOption>
                    <IonSelectOption value="General Fitness">General Fitness</IonSelectOption>
                    <IonSelectOption value="Muscle Gain">Muscle Gain</IonSelectOption>
                    <IonSelectOption value="Couch to 5k">Couch to 5k</IonSelectOption>
                  </IonSelect>
                </IonItem>

                {/* Existing Exercises */}
                <IonItem>
                  <IonLabel position="stacked">
                    Existing Exercises
                    {isLoadingExercises && <span style={{ fontSize: '12px', color: colors.warmGray, marginLeft: '8px' }}>(Loading...)</span>}
                  </IonLabel>
                  <IonTextarea
                    value={existingExercises}
                    placeholder="Enter exercises you already do (comma-separated)"
                    rows={3}
                    onIonInput={(e) => setExistingExercises(e.detail.value)}
                  />
                  {availableExercises.length === 0 && !isLoadingExercises && (
                    <div style={{ fontSize: '12px', color: colors.warmGray, marginTop: '4px' }}>
                      No exercise data found. You can still generate a plan.
                    </div>
                  )}
                </IonItem>

                {/* Workout Frequency */}
                <IonItem>
                  <IonLabel position="stacked">Workout Frequency (times per week) *</IonLabel>
                  <IonInput
                    type="number"
                    value={workoutFrequency}
                    placeholder="3"
                    min="1"
                    max="7"
                    onIonInput={(e) => setWorkoutFrequency(parseInt(e.detail.value) || 3)}
                  />
                </IonItem>

                {/* Restrictions */}
                <IonItem>
                  <IonLabel position="stacked">Any other restrictions or considerations</IonLabel>
                  <IonTextarea
                    value={restrictions}
                    placeholder="e.g., left knee pain, no barbell, max 40 min/session, apartment gym only"
                    rows={3}
                    onIonInput={(e) => setRestrictions(e.detail.value)}
                  />
                </IonItem>

                <IonItem lines="none">
                  <IonLabel>Do you want to automatically email the report to the Member when it's ready?</IonLabel>
                  <IonCheckbox
                    slot="end"
                    checked={emailToMember}
                    onIonChange={(e) => setEmailToMember(e.detail.checked)}
                  />
                </IonItem>

                {/* Generate Button */}
                <IonButton
                  expand="block"
                  onClick={handleGeneratePlan}
                  disabled={isGeneratingPlan || !primaryGoal}
                  style={{ marginTop: '16px' }}
                >
                  {isGeneratingPlan ? (
                    <>
                      <IonSpinner name="crescent" style={{ marginRight: '8px' }} />
                      Generating Plan...
                    </>
                  ) : (
                    'Generate Exercise Plan'
                  )}
                </IonButton>
              </div>

              {/* Results Section */}
              {exercisePlan && (
                <div className="results-section" style={{ marginTop: '32px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                    <div>
                      <h2 style={{ margin: 0 }}>Your 14-Day Exercise Plan</h2>
                      {memberFullName && (
                        <div style={{ marginTop: '4px', fontSize: '14px', color: colors.warmGray }}>
                          {memberFullName}
                        </div>
                      )}
                    </div>
                    <IonButton
                      onClick={handlePrint}
                      color="primary"
                      fill="solid"
                    >
                      <IonIcon icon={downloadOutline} slot="start" />
                      Save as PDF
                    </IonButton>
                  </div>

                  <div style={{
                    background: '#fff3cd',
                    border: '2px solid #ffc107',
                    borderLeft: '4px solid #ff9800',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '24px',
                    fontSize: '13px',
                    lineHeight: '1.7',
                    color: '#856404'
                  }}>
                    <strong>Important Notice:</strong> This exercise plan was generated by artificial intelligence (AI). If you experience any pain, discomfort, dizziness, shortness of breath, or any other concerning symptoms during exercise, <strong>stop immediately</strong> and contact Infinity Medical Concierge for medical guidance. Infinity Medical Concierge can also refer the Member to a biokineticist to help with a personalised exercise plan.
                  </div>
                  
                  <div ref={printRef}>
                  
                  {/* Plan Description */}
                  {exercisePlan.PlanDescription && (
                    <IonCard style={{ marginBottom: '24px' }}>
                      <IonCardHeader>
                        <IonCardTitle>About Your Plan</IonCardTitle>
                      </IonCardHeader>
                      <IonCardContent>
                        <p style={{ lineHeight: '1.8', fontSize: '15px', color: colors.darkGray }}>
                          {exercisePlan.PlanDescription}
                        </p>
                      </IonCardContent>
                    </IonCard>
                  )}
                  
                  <div className="plan-days">
                    {(exercisePlan.days || (Array.isArray(exercisePlan) ? exercisePlan : [])).map((day, index) => (
                      <IonCard key={index} className="day-card">
                        <IonCardHeader>
                          <IonCardTitle>
                            {day.Day}: {day.Overview}
                          </IonCardTitle>
                          <div style={{ marginTop: '8px' }}>
                            {day.EstimatedCalories > 0 && (
                              <IonBadge color="secondary">
                                {day.EstimatedCalories} kcal
                              </IonBadge>
                            )}
                          </div>
                          {day.TargetAreas && day.TargetAreas.length > 0 && (
                            <div style={{ marginTop: '8px' }}>
                              {day.TargetAreas.map((area, i) => (
                                <IonBadge key={i} color="tertiary" style={{ marginRight: '4px' }}>
                                  {area}
                                </IonBadge>
                              ))}
                            </div>
                          )}
                        </IonCardHeader>
                        <IonCardContent>
                          <div className="day-description">
                            {Array.isArray(day.Description) && day.Description.length > 0 ? (
                              day.Description.map((segment, segIndex) => (
                                <div key={segIndex} className="description-segment" style={{ 
                                  marginBottom: segIndex < day.Description.length - 1 ? '16px' : '0',
                                  padding: '12px',
                                  backgroundColor: colors.rgba(colors.lightPurpleGray, 0.15),
                                  borderRadius: '8px',
                                  borderLeft: `4px solid ${colors.blueGray}`
                                }}>
                                  <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    marginBottom: '8px',
                                    gap: '8px'
                                  }}>
                                    <IonBadge color="light" style={{ textTransform: 'capitalize' }}>
                                      {segment.type}
                                    </IonBadge>
                                    <span style={{ 
                                      fontSize: '12px', 
                                      color: colors.warmGray,
                                      fontWeight: '500'
                                    }}>
                                      Step {segment.order}
                                    </span>
                                  </div>
                                  <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', marginBottom: segment.exercises && segment.exercises.length > 0 ? '12px' : '0' }}>
                                    {segment.description}
                                  </div>
                                  
                                  {/* Sub-exercises */}
                                  {segment.exercises && segment.exercises.length > 0 && (
                                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${colors.rgba(colors.warmGray, 0.4)}` }}>
                                      <div style={{ fontSize: '13px', fontWeight: '600', color: colors.darkGray, marginBottom: '8px' }}>
                                        Exercises:
                                      </div>
                                      {segment.exercises.map((exercise, exIndex) => {
                                        // Get exercise link - use provided link or generate one
                                        const exerciseLink = exercise.link || getExerciseLink(exercise.name);
                                        const linkLabel = getExerciseLinkLabel(exerciseLink);
                                        
                                        return (
                                          <div key={exIndex} style={{ 
                                            marginBottom: exIndex < segment.exercises.length - 1 ? '12px' : '0',
                                            padding: '8px',
                                            backgroundColor: colors.rgba(colors.lightPeach, 0.2),
                                            borderRadius: '6px',
                                            borderLeft: `3px solid ${colors.greenGray}`
                                          }}>
                                            <div style={{ 
                                              display: 'flex', 
                                              alignItems: 'center', 
                                              marginBottom: '4px',
                                              gap: '8px',
                                              flexWrap: 'wrap'
                                            }}>
                                              <span style={{ 
                                                fontSize: '12px', 
                                                fontWeight: '600',
                                                color: colors.greenGray,
                                                minWidth: '20px'
                                              }}>
                                                {exercise.order}.
                                              </span>
                                              <span style={{ 
                                                fontSize: '13px', 
                                                fontWeight: '600',
                                                color: colors.darkGray
                                              }}>
                                                {exercise.name}
                                              </span>
                                              {exerciseLink && (
                                                <a
                                                  href={exerciseLink}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  style={{
                                                    fontSize: '11px',
                                                    color: colors.blueGray,
                                                    textDecoration: 'none',
                                                    fontWeight: '500',
                                                    marginLeft: '8px',
                                                    padding: '2px 6px',
                                                    border: `1px solid ${colors.blueGray}`,
                                                    borderRadius: '4px',
                                                    backgroundColor: 'white'
                                                  }}
                                                  onMouseEnter={(e) => {
                                                    e.target.style.backgroundColor = colors.blueGray;
                                                    e.target.style.color = 'white';
                                                  }}
                                                  onMouseLeave={(e) => {
                                                    e.target.style.backgroundColor = 'white';
                                                    e.target.style.color = colors.blueGray;
                                                  }}
                                                >
                                                  {linkLabel} ↗
                                                </a>
                                              )}
                                            </div>
                                            <div style={{ 
                                              fontSize: '12px', 
                                              color: colors.warmGray,
                                              lineHeight: '1.5',
                                              marginLeft: '28px',
                                              whiteSpace: 'pre-wrap'
                                            }}>
                                              {exercise.description}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              ))
                            ) : (
                              <div style={{ whiteSpace: 'pre-wrap' }}>
                                {typeof day.Description === 'string' ? day.Description : 'No description available'}
                              </div>
                            )}
                          </div>
                        </IonCardContent>
                      </IonCard>
                    ))}
                  </div>
                  </div>
                </div>
              )}

              {/* Prompts Toggle - Only show if prompts are available from backend */}
              {promptsUsed.system && promptsUsed.system.trim() !== '' && (
                <div className="prompts-section" style={{ marginTop: '32px' }}>
                  <IonButton
                    fill="clear"
                    expand="block"
                    onClick={() => setShowPrompts(!showPrompts)}
                    style={{ 
                      marginBottom: '16px',
                      '--color': colors.warmGray,
                      fontSize: '14px'
                    }}
                  >
                    <IonIcon 
                      icon={showPrompts ? chevronUpOutline : chevronDownOutline} 
                      style={{ marginRight: '8px' }}
                    />
                    {showPrompts ? 'Hide' : 'Show'} Prompts Used
                  </IonButton>

                  {showPrompts && (
                    <div className="prompts-display">
                      <IonCard>
                        <IonCardHeader>
                          <IonCardTitle>System Prompt</IonCardTitle>
                        </IonCardHeader>
                        <IonCardContent>
                          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', overflow: 'auto' }}>
                            {promptsUsed.system}
                          </pre>
                        </IonCardContent>
                      </IonCard>
                      <IonCard style={{ marginTop: '16px' }}>
                        <IonCardHeader>
                          <IonCardTitle>User Prompt</IonCardTitle>
                        </IonCardHeader>
                        <IonCardContent>
                          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', overflow: 'auto' }}>
                            {promptsUsed.user}
                          </pre>
                        </IonCardContent>
                      </IonCard>
                    </div>
                  )}
                </div>
              )}
            </IonCardContent>
          </IonCard>

          {/* Generation Modal */}
          <IonModal
            isOpen={showGenerationModal}
            onDidDismiss={() => setShowGenerationModal(false)}
            backdropDismiss={false}
            cssClass="modal-top-position"
          >
            <IonHeader>
              <IonToolbar>
                <IonTitle>Generating Your Plan</IonTitle>
              </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding">
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: '60vh',
                padding: '40px 20px',
                textAlign: 'center'
              }}>
                <IonSpinner name="crescent" style={{ marginBottom: '24px', width: '48px', height: '48px' }} />
                <h2 style={{ 
                  marginBottom: '16px', 
                  color: colors.darkGray,
                  fontSize: '20px',
                  fontWeight: '600'
                }}>
                  Generating Your Exercise Plan
                </h2>
                <p style={{ 
                  fontSize: '16px', 
                  color: colors.warmGray,
                  lineHeight: '1.6',
                  marginBottom: '12px'
                }}>
                  This may take a few minutes.
                </p>
                <p style={{ 
                  fontSize: '16px', 
                  color: colors.warmGray,
                  lineHeight: '1.6'
                }}>
                  The plan will be emailed to you and the Member once complete.
                </p>
              </div>
            </IonContent>
          </IonModal>

          <IonAlert
            isOpen={showDeleteConfirm}
            onDidDismiss={() => setShowDeleteConfirm(false)}
            header="Delete Exercise Plan"
            message="Are you sure you want to delete this plan? This action cannot be undone."
            buttons={[
              { text: 'Cancel', role: 'cancel' },
              {
                text: 'Delete',
                role: 'destructive',
                handler: () => {
                  setShowDeleteConfirm(false);
                  handleDeletePlan();
                }
              }
            ]}
          />

          {/* Toast Container */}
          <ToastContainer
            position="top-center"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </div>
  );

  if (noPageWrapper) {
    return <div className="exercise-plan-content">{content}</div>;
  }

  return (
    <IonPage>
      <IonContent className="exercise-plan-content">
        {content}
      </IonContent>
    </IonPage>
  );
};

export default ExercisePlan;

